import { Button, Chip } from "@mui/material";
import { RefreshData } from "components/helper";
import { format } from "date-fns";
import { formatDisplayDate } from "utils/dateFormat";
import { useMemo } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useEmployeeById,
  useEmployeeTimesheetStats,
  useTimesheets,
  useUpdateTimesheetPayment,
} from "utils/employeeService";
import {
  entryDurationMinutes,
  formatDuration,
  isInCurrentWeek,
  isOpenEntry,
  normalizePaymentStatus,
  PAYMENT_STATUS,
} from "utils/timesheetUtils";
import TimesheetEntriesTable from "./TimesheetEntriesTable";
import TimesheetStatCards from "./TimesheetStatCards";

const EmployeeTimesheet = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: employee,
    isLoading: employeeLoading,
    refetch: refetchEmployee,
  } = useEmployeeById(id);
  const {
    data: timesheetPage,
    isLoading: entriesLoading,
    refetch: refetchEntries,
  } = useTimesheets({
    page: 0,
    size: 500,
    employeeId: id,
    sortBy: "clockIn",
    sortDirection: "DESC",
  });
  const { data: remoteStats, refetch: refetchStats } = useEmployeeTimesheetStats(id);

  const updatePaymentMutation = useUpdateTimesheetPayment({
    onSuccess: () => toast.success("Payment status updated"),
    onError: (error) =>
      toast.error(error?.response?.data?.errorMessage || "Failed to update payment status"),
  });

  const entries = timesheetPage?.content || [];

  const stats = useMemo(() => {
    const unpaidEntries = entries.filter(
      (entry) =>
        !isOpenEntry(entry) &&
        normalizePaymentStatus(entry.paymentStatus ?? entry.paidStatus) === PAYMENT_STATUS.UNPAID
    );
    const weekMinutes = entries
      .filter((entry) => isInCurrentWeek(entry.clockIn || entry.clockInAt))
      .reduce((sum, entry) => sum + entryDurationMinutes(entry), 0);
    const unpaidMinutes = unpaidEntries.reduce(
      (sum, entry) => sum + entryDurationMinutes(entry),
      0
    );
    const clockedIn =
      employee?.isClockedIn || employee?.clockedIn || entries.some((entry) => isOpenEntry(entry));
    const monthMinutes = entries
      .filter((entry) => {
        const stamp = entry.clockIn || entry.clockInAt;
        if (!stamp) return false;
        const date = new Date(stamp);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      })
      .reduce((sum, entry) => sum + entryDurationMinutes(entry), 0);

    return {
      clockedInCount: clockedIn ? 1 : 0,
      weekMinutes: remoteStats?.weekMinutes ?? weekMinutes,
      unpaidMinutes: remoteStats?.unpaidMinutes ?? unpaidMinutes,
      unpaidCount: remoteStats?.unpaidCount ?? unpaidEntries.length,
      monthMinutes: remoteStats?.monthMinutes ?? monthMinutes,
    };
  }, [employee, entries, remoteStats]);

  const refreshAll = () => {
    refetchEmployee();
    refetchEntries();
    refetchStats();
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate("/admin/timesheets")}
                  className="text-app-text hover:bg-app-surface-hover"
                  startIcon={<IoArrowBack />}
                >
                  Back
                </Button>
                <div>
                  <h3 className="font-semibold text-lg text-app-text">
                    {employee?.name || "Employee timesheet"}{" "}
                    <RefreshData refreshData={refreshAll} />
                  </h3>
                  <p className="text-sm text-app-text-secondary">
                    {[employee?.jobTitle, employee?.department, employee?.email]
                      .filter(Boolean)
                      .join(" · ") || "Hours, clock history, and payment status"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {stats.clockedInCount ? (
                  <Chip size="small" color="success" label="Clocked in" />
                ) : null}
                <Chip
                  size="small"
                  variant="outlined"
                  color={
                    employee?.status === "ACTIVE" || employee?.status === true
                      ? "success"
                      : "default"
                  }
                  label={
                    employee?.status === "ACTIVE" || employee?.status === true
                      ? "Active"
                      : "Inactive"
                  }
                />
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(`/admin/timesheets/edit/${id}`, { state: { item: employee } })
                  }
                >
                  Edit employee
                </Button>
              </div>
            </div>
          </div>
          <div className="px-4 pb-4">
            <TimesheetStatCards
              employeeCount={1}
              clockedInCount={stats.clockedInCount}
              weekMinutes={stats.weekMinutes}
              unpaidMinutes={stats.unpaidMinutes}
              unpaidCount={stats.unpaidCount}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg border border-app-border p-4">
                <p className="text-xs uppercase text-app-text-muted">
                  {employee?.payType === "HOURLY" ? "Hourly rate" : "Monthly salary"}
                </p>
                <p className="text-lg font-semibold">
                  {employee?.payType === "HOURLY"
                    ? `$${employee?.hourlyRate || "0"}`
                    : `$${employee?.monthlySalary || employee?.hourlyRate || "0"}`}
                </p>
                <p className="text-xs text-app-text-secondary mt-1">
                  {employee?.employmentType === "PART_TIME" ? "Part-time" : "Full-time"}
                  {employee?.payType !== "HOURLY" && employee?.expectedHoursPerMonth
                    ? ` · ${employee.expectedHoursPerMonth} hrs/month`
                    : ""}
                </p>
              </div>
              <div className="rounded-lg border border-app-border p-4">
                <p className="text-xs uppercase text-app-text-muted">This month</p>
                <p className="text-lg font-semibold">{formatDuration(stats.monthMinutes)}</p>
              </div>
              <div className="rounded-lg border border-app-border p-4">
                <p className="text-xs uppercase text-app-text-muted">Start date</p>
                <p className="text-lg font-semibold">{formatDisplayDate(employee?.startDate)}</p>
              </div>
            </div>
            <TimesheetEntriesTable
              entries={entries}
              isLoading={employeeLoading || entriesLoading}
              showEmployee={false}
              isUpdatingPayment={updatePaymentMutation.isLoading}
              onTogglePayment={(entry, paymentStatus) =>
                updatePaymentMutation.mutate({ id: entry.id, paymentStatus })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTimesheet;
